import { getItemNavigationUrl, isExternalUrl } from './navigationUtils';

/**
 * Converts mobile navigation targets to the same internal `/app/...` path shape.
 *
 * Mobile menu items and bottom-nav tabs can come from different systems:
 * Angular actions (`app.clients`), POV/router paths (`/app/clients`), and
 * legacy menu URLs (`/clients`). Normalizing them lets duplicate filtering and
 * current-tab matching compare paths consistently while ignoring query/hash data.
 */
export const normalizeMobileNavigationPath = (path) => {
  if (!path || typeof path !== 'string' || isExternalUrl(path)) return null;

  const cleanPath = path.split('?')[0].split('#')[0];
  if (cleanPath.startsWith('app.')) {
    return `/app/${cleanPath.replace(/^app\./, '').replace(/\./g, '/')}`;
  }
  if (cleanPath.startsWith('/app/')) return cleanPath;
  if (cleanPath.startsWith('/')) return `/app${cleanPath}`;
  return cleanPath;
};

export const getMenuItemPath = (item) => normalizeMobileNavigationPath(
  item?.url_path || item?.url || getItemNavigationUrl(item),
);

export const getBottomNavTabPaths = (tab) => {
  const paths = [
    tab?.action,
    ...(tab?.extraActions || []),
  ];

  if (tab?.platformNavigationItem) {
    paths.push(getItemNavigationUrl(tab.platformNavigationItem));
  }

  return paths.map(normalizeMobileNavigationPath).filter(Boolean);
};

export const buildPathSet = (paths = []) => new Set(paths.map(normalizeMobileNavigationPath).filter(Boolean));

export const buildBottomNavPathSet = (tabs = []) => new Set(tabs.flatMap(getBottomNavTabPaths));

export const filterMenuItemsByPathSet = (menuItems, pathSet) => {
  if (!menuItems) return menuItems;

  return menuItems.reduce((acc, item) => {
    const itemPath = getMenuItemPath(item);
    if (itemPath && pathSet.has(itemPath)) return acc;

    if (item.submenuItems?.length > 0) {
      const filteredSubs = filterMenuItemsByPathSet(item.submenuItems, pathSet);
      if (filteredSubs.length > 0) {
        acc.push({ ...item, submenuItems: filteredSubs });
      }
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
};

export const filterMenuGroupsByPathSet = (groups, pathSet) => groups
  .map((group) => filterMenuItemsByPathSet(group, pathSet))
  .filter((group) => group.length > 0);

export const filterBottomNavTabsByPathSet = (tabs, pathSet) => tabs.filter((tab) => (
  getBottomNavTabPaths(tab).every((path) => !pathSet.has(path))
));
